import { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "../../../app/helpers";
import type { RootState } from "../../../app/rootReducer";
import { setInputs } from "../../Booking-Form/slices/booking.slice";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import type { VisitorRegistrationEntry } from "../../Booking-Form/typings/booking-inputs";
import { defaultRecurrenceWeekdays } from "../../Booking-Form/typings/shared/weekdays";
import type { UserOrEmailMapping } from "../../Admin/typings/admin.types";
import type { BookingInputs } from "../../../components/FacilityManager/Domain/Types/BookingInputs.type";
import type { IFloorPayload } from "../../../components/FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import type { IZoneSchedule } from "../../../components/FacilityManager/Domain/Types/FloorPlan/ZoneSchedule";
import { BookingServicePrompt } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/BookingServicePrompt.component";
import { useSelectionPrompt2ConferenceState } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/SelectionPrompt/useSelectionPrompt2ConferenceState";
import MsTeamsCallSwitch from "../../../components/BookingForm/msTeams-call-switch.component";
import { TeamVisitorInvitationRegistrationsPanel } from "../../../components/BookingForm/partials/team-visitor-invitation-registrations-panel.component";
import {
  filterParticipantsOpt,
  inputDialogEditParticipants,
  isOptionEqualToVal,
  mergeRequiredAndOptionalUsers,
  renderParticiapantTags
} from "../../../components/BookingForm/functions/form-zone-restriction.functions";
import { getOptionLabelOfUserOrEmailMapping } from "../../../utils/type/type.utils";
import { useRemoteFetchUserInfoForAllUsers } from "../../../hooks/Remote/User/UserInfo/useRemoteFetchUserInfoForAllUsers";
import { BookingObjectFavoriteButton } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/SelectionPrompt/partial/booking-object-favorite-button.component";
import { colleagueFloorConferenceBookBlockReason } from "./colleague-workplace-floor.functions";
import type { ColleagueFloorConferenceDraft } from "./colleague-workplace-floor.types";

const BLOCK_REASON_KEYS = {
  loading: "_colleagueFloor_conferenceServicesLoading",
  title: "_colleagueFloor_conferenceTitleRequired",
  service: "_colleagueFloor_conferenceServiceRequired"
} as const;

type ConfirmProps = {
  zone: IZoneSchedule;
  floorPlan: IFloorPayload;
  whenLabel: string;
  locationInventoryId: number;
  locationName?: string;
  date: string;
  startTime: string;
  endTime: string;
  timezone: string;
  isSubmitting: boolean;
  onCancel: () => void;
  onConfirm: (draft: ColleagueFloorConferenceDraft) => void;
};

function participantOptions(
  people: {
    userId: string;
    firstName: string;
    surname: string;
    email: string;
    companyId: number;
  }[],
  currentUserId: string
): UserOrEmailMapping[] {
  return people
    .filter(person => person.userId !== currentUserId)
    .map(person => ({
      userId: person.userId,
      firstName: person.firstName,
      surname: person.surname,
      email: person.email,
      companyId: person.companyId
    }));
}

/**
 * Conference booking sheet over the colleague floor.
 * Room, date, and time are already chosen. Cancel posts nothing.
 */
export function ColleagueFloorConferenceConfirm({
  zone,
  floorPlan,
  whenLabel,
  locationInventoryId,
  locationName,
  date,
  startTime,
  endTime,
  timezone,
  isSubmitting,
  onCancel,
  onConfirm
}: Readonly<ConfirmProps>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const {
    userInformation: { sub, msGraphEnabled, visitorManagementEnabled, company }
  } = useSelector((state: RootState) => state.login);
  const bookingDraft = useSelector((state: RootState) => state.booking.inputs);
  const draftSnapshot = useRef(bookingDraft);
  const onlineMeetingProvider = bookingDraft.onlineMeetingProvider;
  const conferenceService = bookingDraft.conferenceService;
  const { data: remoteUsers } = useRemoteFetchUserInfoForAllUsers();

  const [subject, setSubject] = useState("");
  const [subjectTouched, setSubjectTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [requiredGuests, setRequiredGuests] = useState<UserOrEmailMapping[]>([]);
  const [optionalGuests, setOptionalGuests] = useState<UserOrEmailMapping[]>([]);
  const [showOptional, setShowOptional] = useState(false);
  const [visitorsOpen, setVisitorsOpen] = useState(false);
  const [visitorRegistrations, setVisitorRegistrations] = useState<VisitorRegistrationEntry[]>([]);

  const inventoryId = zone.inventory?.id ?? zone.inventoryId;
  const roomName = zone.inventory?.name?.trim() || t("_colleagueFloor_conferenceRoom");
  const showTeams = msGraphEnabled === true && company?.meta?.calendarSyncPossible === true;
  const showVisitors = visitorManagementEnabled === true;

  const bookingInputs = useMemo<BookingInputs>(
    () => ({
      mode: BookingType.CONFERENCEZONE,
      bookingType: BookingType.CONFERENCEZONE,
      bookingFrom: date,
      bookingTo: date,
      bookingStart: startTime,
      bookingEnd: endTime,
      timezone,
      selectedLocation: locationInventoryId,
      weekdays: defaultRecurrenceWeekdays,
      usersBookedFor: [{ userId: sub, isExternal: false, bookingInventoryId: inventoryId }],
      zoneAccess: null
    }),
    [date, endTime, inventoryId, locationInventoryId, startTime, sub, timezone]
  );

  const services = useSelectionPrompt2ConferenceState({
    selected: { place: undefined, zone },
    bookingInputs,
    floorPlanForService: floorPlan,
    selectedZone: inventoryId,
    userId: sub,
    selectableByVariant: true
  });

  useEffect(() => {
    const snapshot = draftSnapshot.current;
    return () => {
      dispatch(
        setInputs({
          onlineMeetingProvider: snapshot.onlineMeetingProvider,
          conferenceService: snapshot.conferenceService,
          visitorRegistrations: snapshot.visitorRegistrations,
          subject: snapshot.subject,
          description: snapshot.description,
          zoneAccess: snapshot.zoneAccess
        })
      );
    };
  }, [dispatch]);

  const block = colleagueFloorConferenceBookBlockReason({
    subject,
    servicesLoading: services.activateBookingServices === true && services.isBookingServiceLoading,
    mandatoryServiceMissing:
      services.activateBookingServices === true &&
      services.derived?.mandatoryConferenceServiceIncomplete === true
  });
  const bookDisabled = block != null || isSubmitting;
  const showCateringAction =
    services.activateBookingServices === true &&
    services.derived?.isConferenceServicesActionEnabled === true &&
    services.derived?.hasMandatory !== true;

  const options = participantOptions(remoteUsers ?? [], sub);

  const confirm = () => {
    if (bookDisabled) {
      return;
    }
    onConfirm({
      inventoryId,
      subject: subject.trim(),
      description,
      guests: mergeRequiredAndOptionalUsers(requiredGuests, optionalGuests),
      visitorRegistrations,
      onlineMeetingProvider: showTeams ? onlineMeetingProvider ?? null : null,
      conferenceService
    });
  };

  return (
    <Box
      data-testid="colleague-floor-conference-confirm"
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 3,
        display: "flex",
        alignItems: narrow ? "stretch" : "center",
        justifyContent: "center",
        bgcolor: "rgba(0, 0, 0, 0.4)"
      }}
      onClick={() => {
        if (!isSubmitting) {
          onCancel();
        }
      }}
    >
      <Stack
        spacing={2}
        onClick={event => event.stopPropagation()}
        sx={{
          width: narrow ? "100%" : 480,
          maxHeight: "100%",
          bgcolor: "background.paper",
          p: 2,
          display: "flex"
        }}
      >
        <Box sx={{ overflowY: "auto", flex: 1 }}>
          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="h6">{roomName}</Typography>
            <BookingObjectFavoriteButton
              bookingType={BookingType.CONFERENCEZONE}
              bookingInventoryId={inventoryId ?? 0}
              floorInventoryId={floorPlan.floorInventoryId}
            />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {whenLabel}
          </Typography>
          <Stack
            direction={narrow ? "column" : "row"}
            spacing={1.5}
            sx={{ alignItems: narrow ? "stretch" : "flex-end", mb: 2 }}
          >
            <TextField
              autoFocus
              fullWidth
              label={t("_conferenceInvite_meetingDetailsSection")}
              value={subject}
              onBlur={() => setSubjectTouched(true)}
              onChange={event => setSubject(event.target.value)}
              helperText={
                subjectTouched && subject.trim() === ""
                  ? t("_colleagueFloor_conferenceTitleRequired")
                  : undefined
              }
            />
            {showTeams && <MsTeamsCallSwitch enableEdit />}
          </Stack>
          <Typography variant="subtitle1">{t("_conferenceInvite_participantsSection")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
            {t("_conferenceInvite_participantsHelper")}
          </Typography>
          <Autocomplete
            multiple
            freeSolo
            options={options}
            value={requiredGuests}
            filterOptions={filterParticipantsOpt}
            isOptionEqualToValue={isOptionEqualToVal}
            getOptionLabel={getOptionLabelOfUserOrEmailMapping}
            renderTags={renderParticiapantTags}
            onChange={(_event, values, reason, details) => {
              inputDialogEditParticipants(
                reason,
                values,
                details,
                next => setRequiredGuests(next.map(guest => ({ ...guest, isRequired: true }))),
                enqueueSnackbar
              );
            }}
            renderInput={params => (
              <TextField {...params} placeholder={t("Add participants or email")} />
            )}
          />
          <Button
            type="button"
            onClick={() => setShowOptional(open => !open)}
            sx={{ textTransform: "none", px: 0 }}
          >
            {showOptional
              ? t("_conferenceInvite_hideOptionalParticipants")
              : t("_conferenceInvite_addOptionalParticipants")}
          </Button>
          {showOptional && (
            <Autocomplete
              multiple
              freeSolo
              options={options}
              value={optionalGuests}
              filterOptions={filterParticipantsOpt}
              isOptionEqualToValue={isOptionEqualToVal}
              getOptionLabel={getOptionLabelOfUserOrEmailMapping}
              renderTags={renderParticiapantTags}
              onChange={(_event, values, reason, details) => {
                inputDialogEditParticipants(
                  reason,
                  values,
                  details,
                  next => setOptionalGuests(next.map(guest => ({ ...guest, isRequired: false }))),
                  enqueueSnackbar
                );
              }}
              renderInput={params => (
                <TextField {...params} placeholder={t("Add optional participants or email")} />
              )}
            />
          )}
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            sx={{ mt: 2 }}
            placeholder={t("Add a description")}
            value={description}
            onChange={event => setDescription(event.target.value)}
          />
          {showVisitors && (
            <Box sx={{ mt: 2 }}>
              <Button
                type="button"
                data-testid="colleague-floor-add-visitors"
                onClick={() => setVisitorsOpen(open => !open)}
                sx={{ textTransform: "none", px: 0 }}
              >
                {visitorsOpen
                  ? t("_conferenceInvite_hideVisitors")
                  : t("_conferenceInvite_addVisitors")}
              </Button>
              <TeamVisitorInvitationRegistrationsPanel
                bookerSub={sub}
                derivedVisitOnlyLocationLabel={locationName}
                hideVisitOnlyLocation
                onDraftChange={setVisitorRegistrations}
                showFooterActions={false}
                visitOnlyOnly
                visible={visitorsOpen}
              />
            </Box>
          )}
          {showCateringAction && (
            <Button
              type="button"
              data-testid="colleague-floor-add-services"
              onClick={() => services.setIsServiceModalOpen(true)}
              sx={{ textTransform: "none", px: 0, mt: 1 }}
            >
              {t("_colleagueFloor_addCatering")}
            </Button>
          )}
        </Box>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: "center", justifyContent: "flex-end" }}
        >
          {block != null && (
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              {t(BLOCK_REASON_KEYS[block])}
            </Typography>
          )}
          <Button type="button" disabled={isSubmitting} onClick={onCancel}>
            {t("Cancel")}
          </Button>
          <Button
            type="button"
            variant="contained"
            data-testid="colleague-floor-conference-book"
            disabled={bookDisabled}
            onClick={confirm}
          >
            {t("_colleagueFloor_confirmBookRoom")}
          </Button>
        </Stack>
      </Stack>
      {services.activateBookingServices === true && (
        <BookingServicePrompt
          userId={sub}
          bookingInputs={bookingInputs}
          floorPlanForService={floorPlan}
          selectedEntry={zone}
          isServiceAdminBypass={services.isServiceAdminBypass}
          selectedZone={inventoryId}
          remoteMaxSetupTime={services.remoteMaxSetupTime}
          isModalOpen={services.isServiceModalOpen}
          setIsModalOpen={services.setIsServiceModalOpen}
          isBookingServiceLoading={services.isBookingServiceLoading}
          setIsBookingServiceLoading={services.setIsBookingServiceLoading}
        />
      )}
    </Box>
  );
}
